import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { api, call } from './api'

export type Period = 'today' | 'week' | 'month' | 'quarter' | 'year'
export type Place = { state?: string; region?: string; outletId?: string }

const clean = <T extends Record<string, unknown>>(o: T) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as { [K in keyof T]: string }

export const locationsQuery = queryOptions({ queryKey: ['locations'], queryFn: () => call(api.locations.$get()), staleTime: Infinity })

export const meQuery = queryOptions({ queryKey: ['me'], queryFn: () => call(api.me.$get()), staleTime: 5 * 60_000, retry: false })

export const dashboardQuery = (period: Period) =>
  queryOptions({ queryKey: ['dashboard', period], queryFn: () => call(api.dashboard.$get({ query: { period } })) })

export type CustomerFilters = Place & { q?: string; page?: number; level?: 'full' | 'mid' | 'low'; status?: 'pending'; sort?: 'name' | 'recent' | 'level' | 'lastOrder' }
export const customersQuery = (f: CustomerFilters) =>
  queryOptions({
    queryKey: ['customers', f],
    queryFn: () => call(api.customers.$get({ query: clean({ ...f, page: f.page ? String(f.page) : undefined }) as never })),
    placeholderData: keepPreviousData,
  })

export const customerQuery = (id: string) =>
  queryOptions({ queryKey: ['customer', id], queryFn: () => call(api.customers[':id'].$get({ param: { id } })) })

export type OrderFilters = Place & { q?: string; page?: number; status?: string; period?: Period }
export const ordersQuery = (f: OrderFilters) =>
  queryOptions({
    queryKey: ['orders', f],
    queryFn: () => call(api.orders.$get({ query: clean({ ...f, page: f.page ? String(f.page) : undefined }) as never })),
    placeholderData: keepPreviousData,
  })

export const outletsQuery = queryOptions({ queryKey: ['outlets'], queryFn: () => call(api.outlets.$get()), staleTime: 60_000 })
const getOutlet = (id: string, period: Period) => call(api.outlets[':id'].$get({ param: { id }, query: { period } }))
export type OutletData = Awaited<ReturnType<typeof getOutlet>>
export const outletQuery = (id: string, period: Period) => queryOptions({ queryKey: ['outlet', id, period], queryFn: () => getOutlet(id, period) })

export const notificationsQuery = queryOptions({ queryKey: ['notifications'], queryFn: () => call(api.notifications.$get()) })
export const portalQuery = queryOptions({ queryKey: ['portal'], queryFn: () => call(api.portal.$get()) })
export const directoryQuery = queryOptions({ queryKey: ['portal', 'businesses'], queryFn: () => call(api.portal.businesses.$get()), staleTime: 5 * 60_000 })

export const businessQuery = queryOptions({ queryKey: ['settings', 'business'], queryFn: () => call(api.business.$get()) })
export const teamQuery = queryOptions({ queryKey: ['settings', 'team'], queryFn: () => call(api.team.$get()) })
export const keysQuery = queryOptions({ queryKey: ['settings', 'keys'], queryFn: () => call(api.keys.$get()) })
export const devicesQuery = queryOptions({ queryKey: ['settings', 'devices'], queryFn: () => call(api.devices.$get()) })
